import React from 'react'
import NextImage from 'next/image'
import Link from 'next/link'

import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button-v1'
import { Badge } from '@wartek-id/badge'

import {
  useQueryAPIGetActiveSelection,
  useQueryAPIGetKSSelectionCandidate,
  useQueryAPIGetKSSelectionProposal,
} from 'api/bcks/useAPIBCKSInvitation'
import useKSPSTrackEvent from 'app/BCKSPage/hooks/useKSPSTrackEvent'
import { formatDate } from 'utils/datetime'

import type { ResponseSuccessKSSelectionCandidate } from 'api/bcks/useAPIBCKSInvitation'

interface InvitationStateMap {
  title: string
  buttonText?: string
  imageSrc: string
  targetUrl?: string
}

const getUIKSStateMapOld = (
  candidateData: ResponseSuccessKSSelectionCandidate,
  deadlineDate: string
): InvitationStateMap => {
  const {
    selectionCandidateState: state,
    selectionState,
    selectionType,
  } = candidateData ?? {}

  switch (state) {
    case 'invitation':
      return {
        title: 'Anda terpilih untuk mengikuti Seleksi Administrasi!',
        buttonText: 'Cek Informasi Resmi',
        imageSrc: '/images/bcks/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation',
      }
    case 'notInvited':
      return {
        title:
          'Seleksi Kepala Sekolah sedang berlangsung untuk Guru yang memenuhi syarat',
        buttonText: 'Cek cara usulkan diri',
        imageSrc: '/images/bcks/img-collect-files-folder.png',
        targetUrl: '/bcks/self-proposal',
      }
    case 'documentSubmission':
      const shownDate = new Date(deadlineDate)
        .toLocaleString('id-ID', {
          timeZone: 'Asia/Jakarta',
          dateStyle: 'long',
          timeStyle: 'short',
        })
        .replace('.', ':') // Replace period in time, ex: 23.59 to 23:59
      return {
        title: `Kumpulkan berkas maksimal ${shownDate} WIB`,
        buttonText: 'Cek',
        imageSrc: '/images/bcks/img-invitation-default.png',
        targetUrl: '/bcks/invitation/precollection',
      }
    case 'documentVerification':
    case 'documentVerificationFailed':
      if (
        selectionType === 'administration' &&
        selectionState === 'documentVerification'
      ) {
        return {
          title:
            'Dalam proses pengecekan berkas. Mohon cek rincian tahapan secara berkala.',
          buttonText: 'Cek rincian tahapan',
          imageSrc: '/images/bcks/img-files-magnifying-glass-progress.png',
          targetUrl: '/bcks/invitation/final',
        }
      }
      break
    case 'adminFinished':
    case 'adminFailed':
    case 'adminCanceled':
      if (selectionType === 'administration') {
        if (selectionState === 'documentVerification') {
          return {
            title:
              'Dalam proses pengecekan berkas. Mohon cek rincian tahapan secara berkala.',
            buttonText: 'Cek rincian tahapan',
            imageSrc: '/images/bcks/img-files-magnifying-glass-progress.png',
            targetUrl: '/bcks/invitation/final',
          }
        }
        if (selectionState === 'finished') {
          return {
            title: 'Informasi tentang Seleksi Administrasi Anda sudah tersedia',
            buttonText: 'Cek',
            imageSrc: '/illustrations/img-collect-files-folder.png',
            targetUrl: '/bcks/invitation/final',
          }
        }
      }
      break
    case 'substanceInProgress':
      if (selectionType === 'substance' && selectionState === 'finished') {
        return {
          title: 'Anda lolos Seleksi Administrasi! Segera unggah foto peserta.',
          buttonText: 'Cek',
          imageSrc: '/illustrations/img-collect-files-folder.png',
          targetUrl: '/bcks/invitation/final',
        }
      }
      break
    case 'substanceFinished':
    case 'diklatPending':
    case 'diklatInProgress':
      return {
        title:
          'Anda lulus Seleksi Substansi! Silahkan tunggu informasi berikutnya',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'napzaInvitation':
    case 'napzaVerification':
      return {
        title:
          'Unggah dokumen NAPZA untuk melanjutkan penugasan Kepala Sekolah',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'napzaFinished':
    case 'napzaFailed':
      return {
        title:
          'Dokumen NAPZA sudah diverifikasi. Mohon tunggu informasi selanjutnya.',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'substanceFailed':
      return {
        title: 'Informasi tentang Seleksi substansi Anda sudah tersedia',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'overrideFinished':
    case 'regularFinished':
      if (selectionState === 'finished') {
        return {
          title:
            'Informasi Hasil Seleksi sudah tersedia! Hubungi Dinas untuk selengkapnya.',
          buttonText: '',
          imageSrc: '/illustrations/img-announcement-speaker.png',
          targetUrl: '',
        }
      }
      break
  }

  return undefined
}

const getUIKSStateMap = (
  candidateData: ResponseSuccessKSSelectionCandidate
): InvitationStateMap => {
  const { selectionCandidateState, selectionState, deadlineDate } =
    candidateData ?? {}

  switch (selectionCandidateState) {
    case 'invitation':
      return {
        title: 'Anda terpilih untuk mengikuti Seleksi Administrasi!',
        buttonText: 'Cek Informasi Resmi',
        imageSrc: '/images/bcks/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation',
      }
    case 'notInvited':
      return {
        title:
          'Seleksi Kepala Sekolah sedang berlangsung untuk Guru yang memenuhi syarat',
        buttonText: 'Cek cara usulkan diri',
        imageSrc: '/images/bcks/img-collect-files-folder.png',
        targetUrl: '/bcks/self-proposal',
      }
    case 'documentSubmission':
      const shownDate = new Date(deadlineDate)
        .toLocaleString('id-ID', {
          timeZone: 'Asia/Jakarta',
          dateStyle: 'long',
          timeStyle: 'short',
        })
        .replace('.', ':') // Replace period in time, ex: 23.59 to 23:59
      return {
        title: `Kumpulkan berkas maksimal ${shownDate} WIB`,
        buttonText: 'Cek',
        imageSrc: '/images/bcks/img-invitation-default.png',
        targetUrl: '/bcks/invitation/precollection',
      }
    case 'documentVerification':
    case 'documentVerificationFailed':
      return {
        title:
          'Dalam proses pengecekan berkas. Mohon cek rincian tahapan secara berkala.',
        buttonText: 'Cek rincian tahapan',
        imageSrc: '/images/bcks/img-files-magnifying-glass-progress.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'adminFinished':
    case 'adminFailed':
    case 'adminCanceled':
      if (selectionState === 'documentVerification') {
        return {
          title:
            'Dalam proses pengecekan berkas. Mohon cek rincian tahapan secara berkala.',
          buttonText: 'Cek rincian tahapan',
          imageSrc: '/images/bcks/img-files-magnifying-glass-progress.png',
          targetUrl: '/bcks/invitation/final',
        }
      }
      return {
        title: 'Informasi tentang Seleksi Administrasi Anda sudah tersedia',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'substanceInProgress':
      return {
        title: 'Anda lolos Seleksi Administrasi! Segera unggah foto peserta.',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'substanceFailed':
      return {
        title: 'Informasi tentang Seleksi substansi Anda sudah tersedia',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'substanceFinished':
    case 'diklatPending':
    case 'diklatInProgress':
      return {
        title:
          'Anda lolos Seleksi Substansi! Silahkan tunggu informasi berikutnya',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'diklatFinished':
      return {
        title: 'Anda lolos Pelatihan! Segera unggah Surat Keterangan Seminar',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'diklatFailed':
      return {
        title: 'Informasi tentang Pelatihan Anda sudah tersedia',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'diklatCertificateIssued':
      return {
        title: 'Sertifikat pelatihan Anda sudah tersedia',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'napzaInvitation':
    case 'napzaRegularInvitation':
    case 'napzaVerification':
      return {
        title:
          'Unggah dokumen NAPZA untuk melanjutkan penugasan Kepala Sekolah',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'napzaRegularVerification':
      return {
        title: 'Dokumen Napza Anda sedang diverifikasi oleh dinas',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'napzaFinished':
    case 'napzaFailed':
    case 'napzaRegularFailed':
      return {
        title:
          'Dokumen NAPZA sudah diverifikasi. Mohon tunggu informasi selanjutnya.',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'napzaRegularFinished':
      return {
        title:
          'Dokumen NAPZA sudah terverifikasi. Mohon tunggu Penugasan dari Dinas.',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'overrideFinished':
    case 'regularFinished':
      return {
        title:
          'Informasi Hasil Seleksi sudah tersedia! Hubungi Dinas untuk selengkapnya.',
        buttonText: '',
        imageSrc: '/illustrations/img-announcement-speaker.png',
        targetUrl: '',
      }
  }

  return undefined
}

export const KSSelectionCard = ({
  isKSPenugasanRegulerEnabled,
  isKSSeleksiTertutupEnabled,
}) => {
  const { KSPSTrackEvent } = useKSPSTrackEvent()

  // -------------------------------------------
  // Get selection candidate and its derivatives
  // -------------------------------------------
  const { data: candidateData, isError: isErrorGetKSSelectionCandidate } =
    useQueryAPIGetKSSelectionCandidate()
  const { data: proposalData, isError: isErrorGetProposalData } =
    useQueryAPIGetKSSelectionProposal({
      enabled: isErrorGetKSSelectionCandidate,
      staleTime: 60 * 60 * 1000, // Stale after 60min, force to refetch upon mounting
      useErrorBoundary: false, // Prevent whole page error
    })
  const { data: activeSelection } = useQueryAPIGetActiveSelection(
    {
      selectionType: 'ADMINISTRATION',
    },
    {
      enabled:
        isKSSeleksiTertutupEnabled &&
        isErrorGetKSSelectionCandidate &&
        isErrorGetProposalData,
      useErrorBoundary: false,
    }
  )
  const uiKSState = !!activeSelection
    ? getUIKSStateMap({
        ...candidateData,
        selectionCandidateState: 'notInvited',
      })
    : isKSPenugasanRegulerEnabled
    ? getUIKSStateMap(candidateData)
    : getUIKSStateMapOld(candidateData, candidateData?.deadlineDate)
  const hasCandidateSelection =
    (!!candidateData?.deadlineDate ||
      candidateData?.selectionCandidateState === 'finished' ||
      !!activeSelection) &&
    uiKSState
  const hasCandidateToPropose = proposalData?.candidates?.length > 0

  // ------------------
  // Conditional render
  // ------------------
  const shouldShow = hasCandidateSelection || hasCandidateToPropose

  if (!shouldShow) {
    return null
  }

  return (
    <section className="px-4">
      <Text
        variant="heading-md"
        className="mb-2"
        as="h2"
        data-testid="ks-selection-card"
      >
        Seleksi Kepala Sekolah
      </Text>

      <div className="space-y-4">
        {hasCandidateSelection && (
          <div className="rounded-lg shadow p-4 flex justify-between space-x-4 bg-white">
            <div className="flex flex-col items-start gap-4">
              <div className="space-y-2">
                {(['invitation', 'notInvited'].includes(
                  candidateData?.selectionCandidateState
                ) ||
                  hasCandidateToPropose ||
                  !!activeSelection) && (
                  <Badge variant="informational">
                    {formatDate(
                      candidateData?.startDate ?? activeSelection?.startDate
                    )}{' '}
                    -{' '}
                    {formatDate(
                      candidateData?.endDate ?? activeSelection?.endDate
                    )}
                  </Badge>
                )}
                <Text data-testid="ks-selection-card-title">
                  {uiKSState.title}
                </Text>
              </div>

              {uiKSState.buttonText && uiKSState.targetUrl && !activeSelection && (
                <Link href={uiKSState.targetUrl} passHref>
                  <Button
                    as="a"
                    type={undefined}
                    className="min-w-36"
                    color="black"
                    size="sm"
                    data-testid="ks-selection-card-link"
                    onClick={() => {
                      KSPSTrackEvent({
                        event_name: 'click_ksps_banner',
                        additional_info: [
                          {
                            key: 'cta_banner',
                            value: uiKSState.buttonText,
                          },
                        ],
                      })
                    }}
                  >
                    {uiKSState.buttonText}
                  </Button>
                </Link>
              )}
            </div>
            <div className="flex flex-col justify-center shrink-0">
              <NextImage
                data-testid="ks-selection-card-image"
                src={uiKSState.imageSrc}
                width={60}
                height={60}
                alt="Seleksi Kepala Sekolah"
              />
            </div>
          </div>
        )}

        {hasCandidateToPropose && (
          <div className="rounded-lg shadow p-4 flex justify-between space-x-4 bg-white">
            <div className="flex flex-col items-start gap-4">
              <div className="space-y-2">
                {!!proposalData.selectionStartDate && (
                  <Badge variant="informational">
                    {formatDate(proposalData.selectionStartDate)} -{' '}
                    {formatDate(proposalData.selectionEndDate)}
                  </Badge>
                )}
                <Text>
                  Seleksi sedang berlangsung. Anda dapat mengusulkan Guru Anda
                  untuk ikut seleksi.
                </Text>
              </div>

              <Link href="/bcks/ks-proposal" passHref>
                <Button
                  as="a"
                  type={undefined}
                  color="black"
                  size="sm"
                  data-testid="ks-selection-card-link"
                  onClick={() => {
                    KSPSTrackEvent({
                      event_name: 'click_ksps_banner',
                      additional_info: [
                        {
                          key: 'cta_banner',
                          value: 'Usulkan guru',
                        },
                      ],
                    })
                  }}
                >
                  Usulkan Guru
                </Button>
              </Link>
            </div>
            <div className="flex flex-col justify-center shrink-0">
              <NextImage
                src="/images/bcks/img-collect-files-folder.png"
                width={60}
                height={60}
                alt="Seleksi Kepala Sekolah"
              />
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
